import { useState } from "react";
import { useClerk, useUser } from "@clerk/clerk-react";
import { useTranslation } from "react-i18next";
import { NavLink, useNavigate } from "react-router";
import { Menu, Moon, Sun, Monitor, LogOut, Bell, Check, Trash2 } from "lucide-react";
import {
  Button,
  Badge,
  AvatarWithSkeleton,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuGroup,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@cliently/ui";
import { cn } from "@cliently/ui";
import { useUiStore } from "../../stores/ui.store";
import {
  useNotifications,
  useUnreadCount,
  useMarkAsRead,
  useMarkAllAsRead,
  useDeleteNotification,
  useDeleteReadNotifications,
} from "../../hooks/use-notifications";
import { useAuthStore } from "../../stores/auth.store";
import { resolveOrganizationMediaUrl } from "../../pages/pro/settings/media-url";
import { settingsProfessionTranslationKey } from "@cliently/types";
import { getProNotificationPath } from "../../lib/notification-navigation";

/** Icônes barre d’outils : pas de fond plein au survol (évite hover:bg-accent trop marqué selon la teinte). */
const headerToolbarIconClass =
  "inline-flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-md border border-transparent bg-transparent text-muted-foreground outline-none transition-[color,border-color] hover:border-border hover:bg-transparent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0";

const languages = [
  { id: "fr", code: "fr", label: "Français" },
  { id: "en", code: "gb", label: "English" },
  { id: "es", code: "es", label: "Español" },
  { id: "zh", code: "cn", label: "中文" },
] as const;



const FlagImg = ({ code, className }: { code: string; className?: string }) => {
  return (
    <img
      src={`https://flagcdn.com/w40/${code}.png`}
      srcSet={`https://flagcdn.com/w80/${code}.png 2x`}
      alt={code}
      className={cn("inline-block h-4 w-6 rounded-sm object-cover", className)}
    />
  );
}



const timeAgo = (date: string, t: (key: string, opts?: Record<string, unknown>) => string) => {
  const dateMs = new Date(date).getTime();
  if (Number.isNaN(dateMs)) return t("notifications.justNow");
  const diff = Date.now() - dateMs;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return t("notifications.justNow");
  if (mins < 60) return t("notifications.minutesAgo", { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t("notifications.hoursAgo", { count: hours });
  return t("notifications.daysAgo", { count: Math.floor(hours / 24) });
}



export const Header = () => {
  const navigate = useNavigate();
  const { toggleSidebar, theme, setTheme } = useUiStore();
  const { signOut } = useClerk();
  const { user } = useUser();
  const org = useAuthStore((s) => s.org);
  const { t, i18n } = useTranslation();
  const { data: notifications } = useNotifications();
  const { data: unread } = useUnreadCount();
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();
  const deleteNotification = useDeleteNotification();
  const deleteReadNotifications = useDeleteReadNotifications();
  const [notifTab, setNotifTab] = useState<"all" | "unread">("all");
  const orgPhotoUrl = resolveOrganizationMediaUrl(org?.profilePhoto);
  const avatarUrl = orgPhotoUrl ?? user?.imageUrl;
  const avatarPosition = orgPhotoUrl ? org?.profilePhotoPosition : undefined;
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const professionLabel =
    org?.professionType === "GENERIC"
      ? (org.customProfession ?? "").trim() || t("settings.professionGeneric")
      : org?.professionType
        ? (() => {
            const key = settingsProfessionTranslationKey(org.professionType);
            return key ? t(key) : org.professionType;
          })()
        : null;

  const currentLang =
    languages.find((l) => i18n.language.startsWith(l.id)) || languages[0];

  const confirmLogout = () => {
    setLogoutDialogOpen(false);
    void signOut();
  };

  const cycleTheme = () => {
    const next =
      theme === "light" ? "dark" : theme === "dark" ? "system" : "light";
    setTheme(next);
  };

  const ThemeIcon = theme === "light" ? Sun : theme === "dark" ? Moon : Monitor;

  const themeModeLabel =
    theme === "light"
      ? t("nav.themeLight", "Clair")
      : theme === "dark"
        ? t("nav.themeDark", "Sombre")
        : t("nav.themeSystem", "Système");

  return (
    <>
      <header className="bg-background/95 sticky top-0 z-30 flex h-16 items-center justify-between border-b px-4 backdrop-blur">
      <button
        type="button"
        onClick={toggleSidebar}
        className={cn(headerToolbarIconClass, "md:hidden")}
        aria-label={t("nav.toggleSidebar", "Ouvrir/fermer le menu")}
      >
        <Menu className="h-5 w-5" aria-hidden />
      </button>

      <div className="flex-1" />

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={cycleTheme}
          className={headerToolbarIconClass}
          aria-label={`${t(
            "nav.cycleTheme",
            "Changer le thème d'affichage",
          )} (${themeModeLabel})`}
        >
          <ThemeIcon className="h-4 w-4" aria-hidden />
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={cn(headerToolbarIconClass, "relative")}
              aria-label={t("nav.notifications", "Notifications")}
            >
              <Bell className="h-4 w-4" aria-hidden />
              {(unread?.count ?? 0) > 0 && (
                <span className="bg-destructive text-destructive-foreground absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold">
                  {unread!.count > 9 ? "9+" : unread!.count}
                </span>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80" onCloseAutoFocus={(e) => e.preventDefault()}>
            <div className="flex items-center justify-between px-3 py-2">
              <span className="text-sm font-semibold">
                {t("notifications.title")}
              </span>
              <div className="flex items-center gap-1">
                {(unread?.count ?? 0) > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto px-2 py-1 text-xs"
                    onClick={(e) => { e.stopPropagation(); markAllAsRead.mutate(); }}
                  >
                    <Check className="mr-1 h-3 w-3" />
                    {t("notifications.markAllRead")}
                  </Button>
                )}
                {notifications && notifications.some((n) => n.read) && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive h-8 w-8"
                    onClick={(e) => { e.stopPropagation(); deleteReadNotifications.mutate(); }}
                    aria-label={t("notifications.deleteRead")}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
            <div className="flex border-b px-3">
              <Button
                type="button"
                variant="ghost"
                className={cn(
                  "h-auto rounded-none px-0 pb-1.5 text-xs font-medium shadow-none hover:bg-transparent",
                  notifTab === "all"
                    ? "border-primary text-primary border-b-2"
                    : "text-muted-foreground hover:text-foreground",
                )}
                onClick={(e) => { e.stopPropagation(); setNotifTab("all"); }}
              >
                {t("notifications.tabAll")}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className={cn(
                  "ml-4 h-auto rounded-none px-0 pb-1.5 text-xs font-medium shadow-none hover:bg-transparent",
                  notifTab === "unread"
                    ? "border-primary text-primary border-b-2"
                    : "text-muted-foreground hover:text-foreground",
                )}
                onClick={(e) => { e.stopPropagation(); setNotifTab("unread"); }}
              >
                {t("notifications.tabUnread")}
                {(unread?.count ?? 0) > 0 && (
                  <span className="bg-destructive text-destructive-foreground ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold">
                    {unread!.count > 9 ? "9+" : unread!.count}
                  </span>
                )}
              </Button>
            </div>
            <DropdownMenuGroup className="max-h-80 overflow-y-auto">
              {(() => {
                const filtered = notifications
                  ? notifTab === "unread"
                    ? notifications.filter((n) => !n.read)
                    : notifications
                  : [];
                if (filtered.length === 0) {
                  return (
                    <div className="text-muted-foreground px-3 py-6 text-center text-sm">
                      {notifTab === "unread"
                        ? t("notifications.emptyUnread")
                        : t("notifications.empty")}
                    </div>
                  );
                }
                return filtered.slice(0, 20).map((notif) => (
                  <DropdownMenuItem
                    key={notif.id}
                    className={cn(
                      "group flex items-start gap-2 px-3 py-2 data-[highlighted]:bg-primary data-[highlighted]:text-primary-foreground",
                      !notif.read && "bg-primary/5",
                    )}
                    onClick={() => {
                      if (!notif.read) markAsRead.mutate(notif.id);
                      const path = getProNotificationPath(notif);
                      if (path) navigate(path);
                    }}
                  >
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span
                        className={cn(
                          "text-sm group-data-[highlighted]:text-primary-foreground",
                          !notif.read && "font-semibold",
                        )}
                      >
                        {notif.title}
                      </span>
                      <span className="text-muted-foreground group-data-[highlighted]:text-primary-foreground/90 line-clamp-1 text-xs">
                        {notif.message}
                      </span>
                      <span className="text-muted-foreground/60 group-data-[highlighted]:text-primary-foreground/80 text-[10px]">
                        {timeAgo(notif.createdAt, t)}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground hover:text-destructive mt-0.5 h-7 w-7 shrink-0 opacity-0 shadow-none transition-opacity group-hover:opacity-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification.mutate(notif.id);
                      }}
                      aria-label={t("notifications.delete")}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuItem>
                ));
              })()}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={headerToolbarIconClass}
              aria-label={t("nav.changeLanguage", "Changer la langue")}
            >
              <FlagImg code={currentLang.code} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-40">
            {languages.map((lang) => (
              <DropdownMenuItem
                key={lang.id}
                onClick={() => i18n.changeLanguage(lang.id)}
                className={i18n.language.startsWith(lang.id) ? "bg-accent" : ""}
              >
                <FlagImg code={lang.code} />
                <span>{lang.label}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="flex items-center gap-2">
          <NavLink
            to="/settings"
            aria-label={t("nav.settings", "Paramètres")}
            className="inline-flex"
          >
            <AvatarWithSkeleton
              className="h-8 w-8"
              src={avatarUrl}
              alt={user?.fullName ?? ""}
              imagePosition={avatarPosition}
              fallback={
                <span className="text-xs">
                  {user?.firstName?.[0]}
                  {user?.lastName?.[0]}
                </span>
              }
            />
          </NavLink>
          <div className="hidden md:block">
            <p className="text-sm font-medium">
              {user?.firstName} {user?.lastName}
            </p>
            {professionLabel ? (
              <Badge variant="secondary" className="mt-1 text-[10px]">
                {professionLabel}
              </Badge>
            ) : null}
          </div>
        </div>

        <button
          type="button"
          className={headerToolbarIconClass}
          onClick={() => setLogoutDialogOpen(true)}
          aria-label={t("nav.logout", "Se déconnecter")}
        >
          <LogOut className="h-4 w-4" aria-hidden />
        </button>
      </div>
      </header>

      <Dialog
        open={logoutDialogOpen}
        onOpenChange={(open) => setLogoutDialogOpen(open)}
      >
        <DialogContent aria-describedby="logout-dialog-desc" className="sm:max-w-md sm:rounded-xl">
          <DialogHeader className="space-y-2">
            <DialogTitle>
              {t("settings.logout.confirmTitle", "Confirmer la déconnexion ?")}
            </DialogTitle>
            <DialogDescription id="logout-dialog-desc">
              {t(
                "settings.logout.confirmDescription",
                "Vous allez être déconnecté de votre compte. Voulez-vous continuer ?",
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setLogoutDialogOpen(false)}
            >
              {t("common.cancel", "Annuler")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmLogout}
            >
              {t("nav.logout", "Se déconnecter")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
